import { useState, useEffect } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

function CardSetupForm({
  onBeforeConfirm,
  onSuccess,
  onError,
  submitLabel = 'Save card',
  returnUrl,
  disabled = false,
  formId,
  showSubmitButton = true,
  showError = true,
  className,
  onSavingChange,
  onValidityChange,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    onSavingChange?.(saving);
  }, [saving, onSavingChange]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || saving || disabled) return;

    setSaving(true);
    setError('');

    try {
      // 1. Validate the element first, before any async work
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message || 'Please check your card details.');
      }

      // 2. Then create the SetupIntent on your backend
      let clientSecret = null;
      if (onBeforeConfirm) {
        const resolved = await onBeforeConfirm();
        clientSecret = resolved?.client_secret ?? resolved?.clientSecret ?? resolved ?? null;
      }

      if (!clientSecret) {
        throw new Error('Missing client secret for card setup.');
      }

      // 3. Confirm
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret,
        redirect: 'if_required',
        confirmParams: returnUrl ? { return_url: returnUrl } : undefined,
      });
      if (confirmError) {
        throw new Error(confirmError.message || 'Failed to update payment method.');
      }

      if (setupIntent?.status === 'succeeded') {
        await onSuccess?.({ setupIntent });
      }
    } catch (err) {
      const message = err?.message || 'Failed to update payment method.';
      setError(message);
      onError?.(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form id={formId} onSubmit={handleSubmit} className={className}>
      <PaymentElement
        options={{ paymentMethodOrder: ['card'] }}
        onChange={(event) => onValidityChange?.(event.complete)}
      />
      {showError && error && <p style={{ color: 'crimson', marginTop: 12 }}>{error}</p>}
      {showSubmitButton && (
        <button
          type="submit"
          disabled={!stripe || !elements || saving || disabled}
          style={{ marginTop: 16 }}
        >
          {saving ? 'Saving…' : submitLabel}
        </button>
      )}
    </form>
  );
}

export default CardSetupForm;